import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import {
  controlKeys,
  entityKeys,
  jiraIssuesKeys,
  ticketKeys,
} from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import RedAsterisk from "../../../../components/RedAsterisk";
import { Toast } from "../../../../components/Toast";
import {
  buildLinkToGap,
  buildLinkToVulnerability,
} from "../../../../utils/utils";
import { getControlEntityIds } from "../../../posture/controls/components/EntitiesSection/utils";
import { useEntities } from "../../../stack/environment/hooks/useEntities";
import JiraFormSelector from "./form/JiraFormSelector";

const IssueCreationModal = ({
  open,
  onClose,
  fullControl,
  isPerMisconfiguration = false,
  selectedMisconfigurations = [],
  vulnerability = null,
}) => {
  const {
    createJiraIssue,
    createJiraIssuePerMisconfiguration,
    createJSMIncident,
    createJSMIncidentPerMisconfiguration,
    trackEvent,
    fetchEntitiesByVulnerability,
  } = useAPI();
  const colors = useColors();
  const { flattenEntities } = useEntities();
  const formRef = useRef({
    submitForm: () => {},
  });
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);
  const isVulnerability = !!vulnerability;

  const { data: complexMisconfiguredEntitiesByType } = useQuery({
    queryKey: entityKeys.entitiesByVulnerability(
      vulnerability?.vulnerability_id
    ),
    queryFn: async () => {
      try {
        const entities = (
          await fetchEntitiesByVulnerability(vulnerability?.vulnerability_id)
        ).data;
        return entities;
      } catch (error) {
        return {};
      }
    },
    select: (response) => {
      const entities = flattenEntities(response);
      const entitiesByType = {};
      entities.forEach((entity) => {
        if (entity.entity_type in entitiesByType) {
          entitiesByType[entity.entity_type].push(entity);
        } else {
          entitiesByType[entity.entity_type] = [entity];
        }
      });
      return entitiesByType;
    },

    enabled: isVulnerability && vulnerability !== null,
  });

  const vulnerabilityEntityIds = useMemo(() => {
    if (!complexMisconfiguredEntitiesByType) return [];
    return Object.values(complexMisconfiguredEntitiesByType)
      .flat()
      .map((entity) => entity.entity_id);
  }, [complexMisconfiguredEntitiesByType]);

  const issueAutoGeneratedFields = isVulnerability
    ? {
        summary:
          `Fix ${vulnerability?.vulnerability_id}` +
          (vulnerability?.cisa_name ? ` - ${vulnerability?.cisa_name}` : ""),
        description:
          vulnerability?.description?.replace(/`/g, "") ||
          "No issue description",
        labels: vulnerability?.severity ? [vulnerability.severity] : [],
        selectedMisconfigurations: null,
        entity_ids: vulnerabilityEntityIds,
      }
    : {
        summary: fullControl?.name,
        description:
          fullControl?.name_for_gap_state +
          "\n\n*Remediation Steps*\n" +
          fullControl?.remediation.remediation_steps_summary
            .map((step, index) => `${index + 1}. ${step}`)
            .join("\n"),
        labels: [fullControl.tool],
        selectedMisconfigurations: isPerMisconfiguration
          ? selectedMisconfigurations
          : null,
        entity_ids: getControlEntityIds(fullControl),
      };

  // Convert custom fields structure to match backend expectations
  const getTicketCustomFields = (ticket) => {
    if (!ticket) return {};

    // Get all custom field keys (they start with 'customfield_')
    const customFieldKeys = Object.keys(ticket).filter((key) =>
      key.startsWith("customfield_")
    );

    return customFieldKeys.reduce((result, key) => {
      const value = ticket[key];
      if (Array.isArray(value)) {
        // For multi-select fields, extract ids
        result[key] = value.map((item) => item?.id);
      } else if (value && typeof value === "object") {
        // For single-select fields, extract id
        result[key] = value.id;
      }
      // Don't include empty fields
      else if (value) {
        // For primitive values
        result[key] = value;
      }
      return result;
    }, {});
  };

  const handleSuccessToast = (url) => {
    if (isPerMisconfiguration && selectedMisconfigurations.length > 1) {
      Toast.success({
        text: "Issues created successfully!",
      });
    } else {
      Toast.success({
        text: "Issue created successfully!",
        recommended_action: "View",
        recommended_action_callback: () => {
          window.open(url, "_blank");
        },
      });
    }
  };

  const handleCreateIssue = async (ticket) => {
    setIsCreating(true);
    trackEvent("User clicked on create button for Jira issue");

    // A known issue with subtask (requires 'parent' field)
    if (ticket.issueType.name === "Subtask") {
      // NOTE: this track message is monitored by Mixpanel to collect data on user engagement - DO NOT CHANGE IT
      trackEvent("User tried to create a Jira Subtask");
    }

    const customFields = getTicketCustomFields(ticket);
    const linkToGap = isVulnerability
      ? buildLinkToVulnerability(vulnerability?.vulnerability_id)
      : fullControl?.control_id
      ? buildLinkToGap(fullControl?.control_id)
      : "";

    // Prepare issue content with proper custom fields mapping
    const issueContent = {
      summary: ticket.issueSummary,
      description:
        (ticket.description || "No issue description") +
        (ticket.includeEntities
          ? "\n\n_Note: The misconfigured entities can be found in the attached CSV file_"
          : ""),
      project: ticket.projectName.key,
      issue_type: ticket.issueType.name,
      labels: ticket.labels,
      custom_fields: customFields,
      entity_ids:
        ticket.includeEntities && issueAutoGeneratedFields.entity_ids
          ? issueAutoGeneratedFields.entity_ids
          : [],
    };

    // Add assignee if provided
    if (ticket.assignee) {
      issueContent.assignee = ticket.assignee?.displayName;
    }

    if (isVulnerability) {
      issueContent.vulnerability_id = vulnerability?.vulnerability_id;
    }

    if (isPerMisconfiguration) {
      issueContent.misconfiguration_ids = ticket.selectedMisconfigurations.map(
        (misconfig) => misconfig.misconfiguration_id
      );
    }

    if (isPerMisconfiguration) {
      Toast.info("Creating Jira issue for each selected misconfiguration...");
    } else {
      Toast.info("Creating Jira issue...");
    }

    try {
      let response;
      if (isPerMisconfiguration) {
        response = await createJiraIssuePerMisconfiguration(
          issueContent,
          linkToGap
        );
      } else {
        response = await createJiraIssue(issueContent, linkToGap);
      }

      handleSuccessToast(response.data.url);
      onClose();

      if (isPerMisconfiguration) {
        queryClient.invalidateQueries({
          queryKey: jiraIssuesKeys.allJiraIssueMisconfigurations(),
        });
        queryClient.invalidateQueries({
          queryKey: jiraIssuesKeys.jiraMisconfigurationsWithTickets(
            fullControl?.control_id
          ),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ticketKeys.all,
        });
        if (isVulnerability) {
          queryClient.invalidateQueries({
            queryKey: jiraIssuesKeys.jiraIssueVulnerability(
              vulnerability?.vulnerability_id
            ),
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: jiraIssuesKeys.jiraIssueControl(fullControl?.control_id),
          });
          queryClient.invalidateQueries({
            queryKey: controlKeys.controlActivityLogs(fullControl?.control_id),
          });
        }
      }
    } catch (err) {
      Toast.error(
        "Failed to create Jira issue, please contact the support team for more information."
      );
    } finally {
      setIsCreating(false);
    }
  };

  // Create JSM incident
  const handleCreateIncident = async (ticket) => {
    setIsCreating(true);
    trackEvent("User clicked on create button for JSM incident");

    const customFields = getTicketCustomFields(ticket);
    const linkToGap = isVulnerability
      ? buildLinkToVulnerability(vulnerability?.vulnerability_id)
      : fullControl?.control_id
      ? buildLinkToGap(fullControl?.control_id)
      : "";

    let issueContent = {
      project_key: ticket.projectName.key,
      request_type_id: ticket.requestType?.id,
      summary: ticket.issueSummary,
      description:
        (ticket.description || "No issue description") +
        (ticket.includeEntities
          ? "\n\n_Note: The misconfigured entities can be found in the attached CSV file_"
          : ""),
      reporter: ticket.reporter,
      custom_fields: customFields,
      entity_ids: ticket.includeEntities
        ? issueAutoGeneratedFields.entity_ids
        : [],
    };

    if (isVulnerability) {
      issueContent.vulnerability_id = vulnerability?.vulnerability_id;
    }

    if (ticket.priority) {
      issueContent.priority = { name: ticket.priority?.value };
    }

    if (isPerMisconfiguration) {
      issueContent.misconfiguration_ids = ticket.selectedMisconfigurations.map(
        (misconfig) => misconfig.misconfiguration_id
      );
    }

    if (isPerMisconfiguration) {
      Toast.info("Creating Jira issue for each selected misconfiguration...");
    } else {
      Toast.info("Creating Jira issue...");
    }

    try {
      let response;
      if (isPerMisconfiguration) {
        response = await createJSMIncidentPerMisconfiguration(
          issueContent,
          linkToGap
        );
      } else {
        response = await createJSMIncident(issueContent, linkToGap);
      }

      handleSuccessToast(response.data.url);
      onClose();

      if (isPerMisconfiguration) {
        queryClient.invalidateQueries({
          queryKey: jiraIssuesKeys.allJiraIssueMisconfigurations(),
        });
        queryClient.invalidateQueries({
          queryKey: jiraIssuesKeys.jiraMisconfigurationsWithTickets(
            fullControl?.control_id
          ),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ticketKeys.all,
        });
        if (isVulnerability) {
          queryClient.invalidateQueries({
            queryKey: jiraIssuesKeys.jiraIssueVulnerability(
              vulnerability?.vulnerability_id
            ),
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: jiraIssuesKeys.jiraIssueControl(fullControl?.control_id),
          });
          queryClient.invalidateQueries({
            queryKey: controlKeys.controlActivityLogs(fullControl?.control_id),
          });
        }
      }
    } catch (err) {
      Toast.error(
        "Failed to create Jira issue, please contact the support team for more information."
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: colors.jira_modal_bg,
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: 0 }}>
        <Typography variant="subtitle1" color={colors.text_primary}>
          Create Jira Issue
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "&:first-of-type": {
            paddingTop: 0,
          },
        }}
      >
        {/* This Box keeps the sub-titles on top when scrolling down the modal */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: colors.jira_modal_bg,
            zIndex: 1,
            padding: "16px 0",
          }}
        >
          <Box sx={{ marginBottom: "16px" }}>
            <Typography variant="body2" color={colors.text_secondary}>
              {isPerMisconfiguration
                ? "Creating a ticket for each selected misconfiguration"
                : isVulnerability
                ? `Creating a ticket for CVE #${vulnerability?.vulnerability_id}`
                : `Creating a ticket for Gap #${fullControl?.control_id}`}
            </Typography>
          </Box>
          <Box
            sx={{
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Typography variant="caption" color={colors.text_secondary}>
              Required fields are marked with an asterisk{" "}
            </Typography>
            <RedAsterisk />
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          <JiraFormSelector
            ref={formRef}
            setIsFormReady={setIsFormReady}
            fullControl={fullControl}
            issueAutoGeneratedFields={issueAutoGeneratedFields}
            onSubmitJira={handleCreateIssue}
            onSubmitJSM={handleCreateIncident}
          />
        </Box>
      </DialogContent>

      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
          margin: "0 24px 12px",
        }}
      >
        <DialogActions>
          {/* Styling this button in-place until we have a button-text-inherit style button */}
          <Button
            variant="text"
            onClick={onClose}
            disabled={isCreating}
            sx={{
              backgroundColor: colors.button_text_inherit_enabled_bg,
              color: colors.button_text_inherit_enabled_text,
              borderRadius: "10px",
              minWidth: "64px",
              padding: "7px 14.5px",
              "&:hover": {
                backgroundColor: colors.button_text_inherit_hovered_bg,
              },
            }}
          >
            <Typography variant="overline">Cancel</Typography>
          </Button>
          {/* Styling this button in-place until we have a button-contained-primary style button */}
          <Button
            variant="contained"
            onClick={() => {
              formRef.current?.submitForm();
            }}
            disabled={isCreating || !isFormReady}
            startIcon={isCreating ? <LoadingIcon size={8} /> : null}
            sx={{
              backgroundColor: colors.button_contained_primary_enabled_bg,
              color: colors.button_contained_primary_enabled_text,
              borderRadius: "10px",
              minWidth: "64px",
              padding: "7px 14.5px",
              "&:hover": {
                backgroundColor: colors.button_contained_primary_hovered_bg,
              },
            }}
          >
            <Typography variant="overline">
              {isCreating ? "Creating..." : "Create"}
            </Typography>
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
export default IssueCreationModal;
